import {
    JasBoardColumns,
    JasBoardData,
    OccupationCredentialColumns,
    OccupationCredentialData,
    OccupationFunctionColumns,
    OccupationFunctionData,
    OccupationGroupColumns,
    OccupationGroupData,
    OccupationReportColumns,
    OccupationReportData,
    jasBoardEntityName,
    occupationCredentialEntityName,
    occupationFunctionEntityName,
    occupationGroupEntityName,
    occupationReportEntityName,
} from '@juvare/credentialing/backend/config/shared';
import { StorageClient } from '@juvare/platform-sdk-js/StorageClient';
import {
    OccupationCTExportData,
    OccupationCTImportData,
    ProfessionalStatusCTExportData,
    professionalStatusCTImportData,
} from './configurationTransferSchemas';

// Occupation Configuration Transfer mapping utils

export const mapExportOccupationData = async (data: OccupationCTExportData) => {
    const occupationGroupStorageClient = new StorageClient<OccupationGroupData, OccupationGroupColumns>(occupationGroupEntityName);

    if (data.groupId) {
        const occupationGroup = await occupationGroupStorageClient.get(data.groupId);
        data.groupName = occupationGroup.columns.name;
        delete data.groupId;
    }

    const occupationReportStorageClient = new StorageClient<OccupationReportData, OccupationReportColumns>(occupationReportEntityName);

    if (data.reportId) {
        const occupationReport = await occupationReportStorageClient.get(data.reportId);
        data.reportName = occupationReport.columns.name;
        delete data.reportId;
    }

    const occupationFunctionStorageClient = new StorageClient<OccupationFunctionData, OccupationFunctionColumns>(
        occupationFunctionEntityName,
    );

    if (data.functionId) {
        const occupationFunction = await occupationFunctionStorageClient.get(data.functionId);
        data.functionName = occupationFunction.columns.name;
        delete data.functionId;
    }

    const occupationCredentialStorageClient = new StorageClient<OccupationCredentialData, OccupationCredentialColumns>(
        occupationCredentialEntityName,
    );
    const jasBoardStorageClient = new StorageClient<JasBoardData, JasBoardColumns>(jasBoardEntityName);

    const credentials: unknown[] = [];

    if (data.credentials?.length) {
        for (const credential of data.credentials) {
            if (credential.credentialId) {
                const occupationCredential = await occupationCredentialStorageClient.get(credential.credentialId);
                credential.credentialName = occupationCredential.columns.name;
                delete credential.credentialId;
            }

            if (credential.jasBoardId) {
                const jasBoard = await jasBoardStorageClient.get(credential.jasBoardId);
                credential.jasBoardName = jasBoard.columns.name;
                delete credential.jasBoardId;
            }

            credentials.push(credential);
        }
    }

    return { ...data, credentials };
};

export const mapImportOccupationData = async (data: OccupationCTImportData) => {
    const occupationGroupStorageClient = new StorageClient<OccupationGroupData, OccupationGroupColumns>(occupationGroupEntityName);

    if (data.groupName) {
        const occupationGroupList = await occupationGroupStorageClient.list({
            filter: [
                {
                    column: 'name',
                    op: '=',
                    value: data.groupName,
                },
            ],
            limit: 1,
        });

        const occupationGroup = occupationGroupList.rows.at(0);
        if (occupationGroup) {
            data.groupId = occupationGroup.id;
        }

        delete data.groupName;
    }

    const occupationReportStorageClient = new StorageClient<OccupationReportData, OccupationReportColumns>(occupationReportEntityName);

    if (data.reportName) {
        const occupationReportList = await occupationReportStorageClient.list({
            filter: [
                {
                    column: 'name',
                    op: '=',
                    value: data.reportName,
                },
            ],
            limit: 1,
        });

        const occupationReport = occupationReportList.rows.at(0);
        if (occupationReport) {
            data.reportId = occupationReport.id;
        }

        delete data.reportName;
    }

    const occupationFunctionStorageClient = new StorageClient<OccupationReportData, OccupationReportColumns>(occupationFunctionEntityName);

    if (data.functionName) {
        const occupationFunctionlist = await occupationFunctionStorageClient.list({
            filter: [
                {
                    column: 'name',
                    op: '=',
                    value: data.functionName,
                },
            ],
            limit: 1,
        });

        const occupationFunction = occupationFunctionlist.rows.at(0);
        if (occupationFunction) {
            data.functionId = occupationFunction.id;
        }

        delete data.functionName;
    }

    const occupationCredentialStorageClient = new StorageClient<OccupationCredentialData, OccupationCredentialColumns>(
        occupationCredentialEntityName,
    );
    const jasBoardStorageClient = new StorageClient<JasBoardData, JasBoardColumns>(jasBoardEntityName);

    const credentials: unknown[] = [];

    if (data.credentials?.length) {
        for (const credential of data.credentials) {
            if (credential.credentialName) {
                const occupationCredentialList = await occupationCredentialStorageClient.list({
                    filter: [
                        {
                            column: 'name',
                            op: '=',
                            value: credential.credentialName,
                        },
                    ],
                });

                const occupationCredential = occupationCredentialList.rows.at(0);
                if (occupationCredential) {
                    credential.credentialId = occupationCredential.id;
                }
                delete credential.credentialName;
            }

            if (credential.jasBoardName) {
                const jasBoardList = await jasBoardStorageClient.list({
                    filter: [
                        {
                            column: 'name',
                            op: '=',
                            value: credential.jasBoardName,
                        },
                    ],
                });

                const jasBoard = jasBoardList.rows.at(0);
                if (jasBoard) {
                    credential.jasBoardId = jasBoard.id;
                }
                delete credential.jasBoardName;
            }

            credentials.push(credential);
        }
    }

    return { ...data, credentials };
};

// Professional Status Configuration Transfer mapping utils

export const mapExportProfessionalStatusData = async (data: ProfessionalStatusCTExportData) => {
    const occupationGroupStorageClient = new StorageClient<OccupationGroupData, OccupationGroupColumns>(occupationGroupEntityName);

    if (data.occupationGroupIds) {
        const occupationGroups = await occupationGroupStorageClient.list({ filter: [{ id: data.occupationGroupIds, op: 'in' }] });
        data.occupationGroupNames = occupationGroups.rows.map(({ columns }) => columns.name) ?? [];
        delete data.occupationGroupIds;
    }

    return data;
};

export const mapImportProfessionalStatusData = async (data: professionalStatusCTImportData) => {
    const occupationGroupStorageClient = new StorageClient<OccupationGroupData, OccupationGroupColumns>(occupationGroupEntityName);

    if (data.occupationGroupNames) {
        const occupationGroups = await occupationGroupStorageClient.list({
            filter: [
                {
                    column: 'name',
                    op: 'in',
                    value: data.occupationGroupNames,
                },
            ],
        });

        data.occupationGroupIds = occupationGroups.rows.map(({ id }) => id);
        delete data.occupationGroupNames;
    }

    return data;
};
